/**
 * @fileoverview Scout Detail Store
 * @description Keeps the row-level content of each imported report in IndexedDB. The run
 * rollup stays in local storage, but a single consolidated report carries megabytes of
 * narratives, rationale, and evidence that would blow the local storage quota.
 * @module features/ScoutEval/utils/scoutDetailStore
 */

const DB_NAME = 'scout-eval';
const DB_VERSION = 1;
const STORE_NAME = 'run-details';

/**
 * Open the detail database, creating the object store on first use.
 * @returns {Promise<IDBDatabase>} Open database.
 */
function openDatabase() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Run one transaction against the detail store.
 * @param {IDBTransactionMode} mode - Transaction mode.
 * @param {(store: IDBObjectStore) => IDBRequest|null} work - Store operation.
 * @returns {Promise<*>} Request result.
 */
async function withStore(mode, work) {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE_NAME, mode);
      const request = work(transaction.objectStore(STORE_NAME));
      transaction.oncomplete = () => resolve(request ? request.result : undefined);
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Store the detail content of one run.
 * @param {string} runId - Run identifier.
 * @param {Object} details - Detail content keyed by group id.
 * @returns {Promise<void>} Resolves once written.
 * @throws {Error} When the browser rejects the write, usually on quota.
 */
export async function saveRunDetails(runId, details) {
  try {
    await withStore('readwrite', (store) => store.put(details, runId));
  } catch {
    throw new Error('Could not save the detail reports for this file — remove an older run and try again.');
  }
}

/**
 * Read the detail content of one run.
 * @param {string} runId - Run identifier.
 * @returns {Promise<Object>} Detail content keyed by group id, empty when unavailable.
 */
export async function loadRunDetails(runId) {
  try {
    return (await withStore('readonly', (store) => store.get(runId))) || {};
  } catch {
    return {};
  }
}

/**
 * Drop the detail content of every run that is no longer stored.
 * @param {string[]} keepRunIds - Run identifiers to retain.
 * @returns {Promise<void>} Resolves once pruned.
 */
export async function pruneRunDetails(keepRunIds) {
  try {
    const stored = await withStore('readonly', (store) => store.getAllKeys());
    const stale = (stored || []).filter((key) => !keepRunIds.includes(key));
    if (stale.length) {
      await withStore('readwrite', (store) => {
        stale.forEach((key) => store.delete(key));
        return null;
      });
    }
  } catch {
    // Pruning is best effort; stale detail rows are harmless.
  }
}
